import React, { useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { keyframes } from '@mui/system';

// ─── Timing ──────────────────────────────────────────────────────────────────
/** Full animation cycle per truck (seconds). */
const CYCLE = 5.0;
/** Second truck is half a cycle behind the first, so one truck is always visible. */
const HALF = CYCLE / 2;

// ─── Truck dimensions (px) ───────────────────────────────────────────────────
const WHEEL_D    = 20;   // wheel diameter
const ENGINE_W   = 26;   // engine bay / hood width (protrudes at the front)
const ENGINE_H   = 28;   // engine bay height (shorter than cab)
const CAB_W      = 56;   // cab width (sits behind the engine bay)
const CAB_H      = 48;   // cab body height above wheels
const FLATBED_W  = 130;  // trailer / flatbed width
const FLATBED_H  = 14;   // flatbed thickness above wheels
const TRUCK_W    = ENGINE_W + CAB_W + FLATBED_W; // 212 — full truck width

// ─── Shipping-container dimensions (px) ──────────────────────────────────────
const CONT_W  = FLATBED_W - 6; // 124 — sits inside flatbed rails
const CONT_H  = 38;            // container height
const DROP_PX = 90;            // how far the container falls before landing

// ─── Scene ───────────────────────────────────────────────────────────────────
const SCENE_W = 480;   // stage width; overflow:hidden clips trucks off-screen
const ROAD_H  = 8;     // road strip at bottom of stage
/** Distance a truck travels to be fully hidden on either side of the stage. */
const TRAVEL  = 500;

// ─── Derived layout (all y values measured from the TOP of the truck group) ──
//   Ground (group bottom) ─ WHEEL_D ─ flatbed ─ CONT_H ─ group top (y=0)
const GROUP_H     = WHEEL_D + FLATBED_H + CONT_H; // 72 — total group height
const ENGINE_TOP  = GROUP_H - WHEEL_D - ENGINE_H;  //  24 — engine bay top from group top
const CAB_TOP     = GROUP_H - WHEEL_D - CAB_H;    //  4  — cab top from group top
const FLATBED_TOP = GROUP_H - WHEEL_D - FLATBED_H; // 38 — flatbed top (= container bottom when landed)
const WHEEL_TOP   = GROUP_H - WHEEL_D;             // 52 — wheel top from group top
const SCENE_H     = ROAD_H + GROUP_H + DROP_PX + 20; // 190 — stage height

// ─── Horizontal positions within the truck group: [ENGINE_BAY][CAB][FLATBED] ─
const CAB_LEFT     = ENGINE_W;           // 26
const FLATBED_LEFT = ENGINE_W + CAB_W;   // 82
const CONT_LEFT    = FLATBED_LEFT + 3;   // 85

// ─── Colors ──────────────────────────────────────────────────────────────────
/** Containers alternate: indigo → orange → indigo → … */
const TRUCK_COLORS = ['rgb(63, 81, 181)', '#F2792B'] as const;
const CAB_COLOR     = '#D1D5DB';
const ENGINE_COLOR  = '#B0B7C3';   // slightly darker than cab
const FLATBED_COLOR = '#4B5563';
const WHEEL_COLOR   = '#1F2937';
const WHEEL_TRIM    = '#6B7280';

// ─── Wheel x-positions within the truck group ─────────────────────────────────
const WHEEL_X_POSITIONS = [
  4,                        // front steer axle (under engine bay)
  CAB_LEFT + CAB_W - 18,   // drive axle A (rear of cab)          → 64
  CAB_LEFT + CAB_W + 8,    // drive axle B (front of flatbed)     → 90
  TRUCK_W - WHEEL_D - 10,  // trailer axle (rear of flatbed)      → 182
];

// ─── Keyframes ───────────────────────────────────────────────────────────────

/**
 * Horizontal truck movement per cycle:
 *   0–30%  : stationary at centre (container loading)
 *  30–49%  : drives left toward exit
 *  49–50%  : fades out before the position jump (prevents a sub-frame flash at centre)
 *  50.01%  : jumps to the right (invisible, hidden by opacity AND overflow:hidden)
 *  73–74%  : fades back in once safely off-screen right
 *  74–92%  : drives from right back to centre
 *  92–100% : stationary at centre (awaiting next container)
 */
const truckRide = keyframes`
  0%     { transform: translateX(0);           opacity: 1; }
  30%    { transform: translateX(0);           opacity: 1; }
  49%    { transform: translateX(-${TRAVEL}px); opacity: 1; }
  50%    { transform: translateX(-${TRAVEL}px); opacity: 0; }
  50.01% { transform: translateX(${TRAVEL}px);  opacity: 0; }
  73%    { transform: translateX(${TRAVEL}px);  opacity: 0; }
  74%    { transform: translateX(${TRAVEL}px);  opacity: 1; }
  92%    { transform: translateX(0);           opacity: 1; }
  100%   { transform: translateX(0);           opacity: 1; }
`;

/**
 * Container vertical drop onto the truck flatbed. The container is a child of
 * the truck group, so the truck's horizontal movement carries it automatically.
 *   0%   : above the stage, invisible — truck is stationary, ready to load
 *   ~6%  : fades in, still falling
 *  24%   : lands on flatbed
 *  26%   : small bounce up
 *  28–50%: settled on truck; rides with it as it drives left
 *  50–51%: snaps hidden (truck has exited; hidden by overflow:hidden anyway)
 *  51–100%: stays hidden so the truck re-enters from the right empty
 */
const containerDrop = keyframes`
  0%   { transform: translateY(-${DROP_PX}px); opacity: 0; }
  6%   { opacity: 1; }
  24%  { transform: translateY(0); opacity: 1; }
  26%  { transform: translateY(-4px); opacity: 1; }
  28%  { transform: translateY(0); opacity: 1; }
  50%  { transform: translateY(0); opacity: 1; }
  51%  { transform: translateY(0); opacity: 0; }
  100% { transform: translateY(0); opacity: 0; }
`;

// ─── Component ───────────────────────────────────────────────────────────────

interface ContainerStackingLoaderProps {
  /** Optional status text shown below the animation. Defaults to 'Analyzing location…' */
  message?: string;
  /** Called when the user dismisses the overlay (× button or Escape key). */
  onCancel?: () => void;
}

/**
 * Full-screen loading overlay shown while the Super Admin "Autofill Location"
 * enrichment API request is in flight (~5–6s).
 *
 * Animation: a shipping container drops onto a freight truck → truck drives left
 * off-screen → a second truck enters from the right → gets its container → drives
 * off → repeat. Two trucks alternate half a cycle apart so one is always visible.
 */
const ContainerStackingLoader = ({ message = 'Analyzing location\u2026', onCancel }: ContainerStackingLoaderProps) => {
  // Close on Escape key.
  useEffect(() => {
    if (!onCancel) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
  <Box
    role="status"
    aria-live="polite"
    aria-label={message}
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: (theme) => theme.zIndex.modal + 10,
      bgcolor: 'rgba(15, 23, 42, 0.82)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    }}
  >
    {/* ── Close button ────────────────────────────────────────────────── */}
    {onCancel && (
      <IconButton
        aria-label="Cancel autofill"
        onClick={onCancel}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
    {/* ── Animation stage ─────────────────────────────────────────────── */}
    <Box sx={{ position: 'relative', width: SCENE_W, height: SCENE_H, overflow: 'hidden' }}>

      {/* Road strip */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        height: ROAD_H,
        bgcolor: '#374151',
        borderRadius: 1,
      }} />

      {/* Dashed centre line on road */}
      <Box sx={{
        position: 'absolute',
        bottom: ROAD_H / 2 - 1,
        width: '100%',
        height: 2,
        backgroundImage:
          'repeating-linear-gradient(90deg, rgba(255,255,255,0.35) 0 28px, transparent 28px 56px)',
      }} />

      {/* Two truck groups — identical keyframe, offset by half a cycle */}
      {TRUCK_COLORS.map((color, i) => (
        <Box
          key={color}
          sx={{
            position: 'absolute',
            bottom: ROAD_H,
            // Centre the truck in the stage so translateX(0) = centred
            left: `calc(50% - ${TRUCK_W / 2}px)`,
            width: TRUCK_W,
            height: GROUP_H,
            animation: `${truckRide} ${CYCLE}s linear infinite`,
            animationDelay: `${-i * HALF}s`,
          }}
        >
          {/* ── Shipping container (drops from above, bounces, then rides) ── */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: CONT_LEFT,
            width: CONT_W,
            height: CONT_H,
            bgcolor: color,
            borderRadius: '3px 3px 0 0',
            // Corrugated-panel texture
            backgroundImage:
              'repeating-linear-gradient(90deg, rgba(0,0,0,0.14) 0 2px, rgba(255,255,255,0.07) 2px 14px)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.45)',
            animation: `${containerDrop} ${CYCLE}s ease-out infinite`,
            animationDelay: `${-i * HALF}s`,
          }} />

          {/* ── Engine bay / hood (front of truck, left of cab) ────────── */}
          <Box sx={{
            position: 'absolute',
            top: ENGINE_TOP,  // 24px from group top
            left: 0,
            width: ENGINE_W,
            height: ENGINE_H,
            bgcolor: ENGINE_COLOR,
            borderRadius: '3px 0 0 0',
          }}>
            {/* Headlight at the very front */}
            <Box sx={{
              position: 'absolute',
              bottom: 8,
              left: 3,
              width: 10,
              height: 6,
              bgcolor: 'rgba(255, 248, 170, 0.95)',
              borderRadius: 1,
            }} />
            {/* Grille: thin horizontal lines */}
            <Box sx={{
              position: 'absolute',
              top: 6,
              left: 4,
              right: 4,
              height: 10,
              backgroundImage:
                'repeating-linear-gradient(180deg, rgba(0,0,0,0.25) 0 1.5px, transparent 1.5px 4px)',
              borderRadius: '1px',
            }} />
          </Box>

          {/* ── Cab (sits behind the engine bay) ────────────────────── */}
          <Box sx={{
            position: 'absolute',
            top: CAB_TOP,   // 4px from group top
            left: CAB_LEFT, // 26px (after engine bay)
            width: CAB_W,
            height: CAB_H,
            bgcolor: CAB_COLOR,
            borderRadius: '4px 0 0 0',
            overflow: 'hidden',
          }}>
            {/* Windshield (front/left face of cab, angled toward engine bay) */}
            <Box sx={{
              position: 'absolute',
              top: 6,
              left: 4,
              width: 16,
              height: 22,
              bgcolor: 'rgba(96, 165, 250, 0.6)',
              borderRadius: '2px 2px 0 0',
            }} />
            {/* Cab door line */}
            <Box sx={{
              position: 'absolute',
              top: 8,
              right: 10,
              width: 2,
              height: '62%',
              bgcolor: 'rgba(0,0,0,0.15)',
              borderRadius: 1,
            }} />
          </Box>

          {/* ── Flatbed / trailer ───────────────────────────────────── */}
          <Box sx={{
            position: 'absolute',
            top: FLATBED_TOP,
            left: FLATBED_LEFT,
            width: FLATBED_W,
            height: FLATBED_H,
            bgcolor: FLATBED_COLOR,
          }} />

          {/* ── Wheels ──────────────────────────────────────────────── */}
          {WHEEL_X_POSITIONS.map((left) => (
            <Box
              key={left}
              sx={{
                position: 'absolute',
                top: WHEEL_TOP,   // 52px from group top
                left,
                width: WHEEL_D,
                height: WHEEL_D,
                borderRadius: '50%',
                bgcolor: WHEEL_COLOR,
                border: `3px solid ${WHEEL_TRIM}`,
                boxSizing: 'border-box',
              }}
            />
          ))}
        </Box>
      ))}
    </Box>

    {/* ── Status label ────────────────────────────────────────────────── */}
    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
      {message}
    </Typography>
  </Box>
  );
};

export default ContainerStackingLoader;
